import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";
import { allSpecials, formatDate, getSpecialFilms } from "@/lib/catalog";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SpecialsPage() {
  usePageMeta(
    "专题合集_日韩伦理片影视策划专题",
    "日韩伦理片编辑策划专题合集，按主题整理精选片单，附入选理由与推荐观看顺序，涵盖家庭伦理、悬疑伦理与短片实验等方向。",
  );

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-shell px-4 pb-9 pt-6 md:px-8 md:pb-12 md:pt-8">
          <Breadcrumbs items={[{ label: "专题" }]} />
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.24em] text-accent-700">
            Curated Specials
          </p>
          <h1 className="mt-2 font-heading text-[32px] font-extrabold leading-tight tracking-tight text-foreground-950 md:text-[42px]">
            专题合集
          </h1>
          <p className="mt-3 max-w-3xl text-[13px] leading-relaxed text-foreground-600 md:text-sm">
            专题不按年份排列，而是按一条线索重排作品。每个企划由编辑撰写导语与入选理由，说明为什么这些作品应该放在一起看，以及从哪一部开始最合适。
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3 font-label text-[11px] text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-folder-open-line text-primary-600" />
              共 {allSpecials.length} 个企划
            </span>
            <span className="h-3 w-[1px] bg-background-300" />
            <span>收录 {allSpecials.reduce((total, item) => total + item.filmIds.length, 0)} 条片单记录</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-shell px-4 pt-10 md:px-8 md:pt-12">
        <SectionHeading
          kicker="All Specials"
          title="全部企划"
          description="点击专题标题进入片单页，可一次看完该主题下的全部作品与推荐顺序。"
        />
        <div className="flex flex-col gap-6">
          {allSpecials.map((special, index) => {
            const specialFilms = getSpecialFilms(special);
            return (
              <article
                key={special.id}
                className="grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:grid-cols-[1.05fr_1fr]"
              >
                <Link
                  to={`/specials/${special.id}`}
                  className="group relative block aspect-[16/10] w-full cursor-pointer overflow-hidden bg-secondary-200 lg:aspect-auto lg:h-full lg:min-h-[280px]"
                >
                  <img
                    src={special.cover}
                    alt={`${special.title} 专题主图`}
                    className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-950/55 via-transparent to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full bg-background-50/95 px-2.5 py-1 font-label text-[10px] font-semibold uppercase tracking-[0.16em] text-primary-900">
                    专题 {String(index + 1).padStart(2, "0")}
                  </span>
                </Link>

                <div className="flex flex-col justify-between p-5 md:p-7">
                  <div>
                    <p className="font-label text-[10px] font-semibold uppercase tracking-[0.2em] text-accent-700">
                      {special.subtitle}
                    </p>
                    <Link
                      to={`/specials/${special.id}`}
                      className="mt-1.5 block cursor-pointer font-heading text-[22px] font-extrabold leading-tight text-foreground-950 transition-colors hover:text-primary-700 md:text-[26px]"
                    >
                      {special.title}
                    </Link>
                    <p className="mt-3 text-[13px] leading-relaxed text-foreground-600">{special.intro}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {special.tags.map((tag) => (
                        <span key={tag} className="rounded-full bg-secondary-100 px-2.5 py-1 text-[10px] text-secondary-900">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5">
                        {specialFilms.slice(0, 5).map((film) => (
                          <span key={film.id} className="relative h-12 w-8 overflow-hidden rounded-sm border border-background-200 bg-secondary-100">
                            <img src={film.poster} alt={film.title} className="h-full w-full object-cover object-top" />
                          </span>
                        ))}
                      </div>
                      <span className="font-label text-[11px] text-foreground-500">
                        {specialFilms.length} 部作品
                      </span>
                    </div>
                    <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-3.5 sm:flex-row sm:items-center sm:justify-between">
                      <p className="font-label text-[11px] text-foreground-500">
                        {special.curator} · {formatDate(special.publishedAt)}
                      </p>
                      <Link
                        to={`/specials/${special.id}`}
                        className="flex w-fit cursor-pointer items-center gap-1.5 rounded-md bg-primary-800 px-3.5 py-2 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-700"
                      >
                        查看策划片单
                        <i className="ri-arrow-right-line text-xs" />
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}